import { Inject, Injectable, Optional } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { REQUEST } from '@nguniversal/express-engine/tokens';

@Injectable({
    providedIn: 'root',
})
export class CanonicalService {
    private readonly supportedLangs = ['en', 'es', 'pt', 'zh-cn'];
    /**
     * Idiomas indexables. /es, /pt y /zh-cn son traducción automática (GTranslate) y van con
     * noindex (ver ssr/server.ts). Cuando haya traducción real en servidor, agregarlos acá y
     * los hreflang recíprocos + x-default vuelven a generarse solos.
     */
    static readonly INDEXABLE_LANGS = ['en'];

    constructor(
        @Inject(DOCUMENT) private readonly doc: Document,
        @Optional() @Inject(REQUEST) private readonly request: any,
    ) {}

    /**
     * Ajusta o crea el link rel="canonical" con la URL absoluta indicada.
     * Si no se pasa url, usa la actual (pathname + origin).
     */
    setCanonical(url?: string): void {
        try {
            const origin = this.getOrigin();
            const href = url && url.trim()
                ? url
                : `${origin}${this.doc.location?.pathname || ''}`;

            let linkEl = this.doc.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
            if (!linkEl) {
                linkEl = this.doc.createElement('link');
                linkEl.setAttribute('rel', 'canonical');
                this.doc.head.appendChild(linkEl);
            }
            linkEl.setAttribute('href', href);
        } catch {
            // ignore errors in non-browser environments
        }
    }

    /**
     * Actualiza canonical + hreflang según la ruta actual.
     * Funciona en browser y SSR.
     */
    updateRouteLinks(rawUrl: string): void {
        const path = this.normalizePath(rawUrl);
        const canonicalPath = CanonicalService.homeAlias(this.ensureLangPath(path));
        this.setCanonical(this.buildAbsolute(canonicalPath));
        this.setHreflangAlternates(canonicalPath);
    }

    setHreflangAlternates(pathWithLang: string): void {
        try {
            const basePath = this.removeLangSuffix(pathWithLang);
            const normalizedBase = basePath === '/' ? '/home' : basePath;

            // Remove previously generated alternates
            const old = this.doc.head.querySelectorAll('link[rel="alternate"][data-seo-managed="true"]');
            old.forEach((node) => node.parentNode?.removeChild(node));

            // hreflang solo tiene sentido con 2+ idiomas indexables.
            const langs = CanonicalService.INDEXABLE_LANGS;
            if (langs.length < 2) return;

            for (const lang of langs) {
                const localized = CanonicalService.homeAlias(`${normalizedBase}/${lang}`);
                const hreflang = lang === 'zh-cn' ? 'zh-CN' : (lang === 'pt' ? 'pt-BR' : lang);
                this.upsertAlternate(hreflang, this.buildAbsolute(localized));
            }

            this.upsertAlternate('x-default', this.buildAbsolute(CanonicalService.homeAlias(`${normalizedBase}/en`)));
        } catch {
            // ignore errors in non-browser environments
        }
    }

    private upsertAlternate(hreflang: string, href: string): void {
        const selector = `link[rel="alternate"][hreflang="${hreflang}"]`;
        let linkEl = this.doc.head.querySelector(selector) as HTMLLinkElement | null;
        if (!linkEl) {
            linkEl = this.doc.createElement('link');
            linkEl.setAttribute('rel', 'alternate');
            linkEl.setAttribute('hreflang', hreflang);
            linkEl.setAttribute('data-seo-managed', 'true');
            this.doc.head.appendChild(linkEl);
        }
        linkEl.setAttribute('href', href);
    }

    private normalizePath(rawUrl: string): string {
        const fromArg = (rawUrl || '').trim();
        const fromDoc = (this.doc.location?.pathname || '').trim();
        let path = fromArg || fromDoc || '/';
        path = path.split('?')[0].split('#')[0] || '/';
        if (!path.startsWith('/')) path = `/${path}`;
        if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
        return path;
    }

    private removeLangSuffix(path: string): string {
        const regex = new RegExp(`/(${this.supportedLangs.join('|')})$`, 'i');
        return path.replace(regex, '') || '/';
    }

    private ensureLangPath(path: string): string {
        const regex = new RegExp(`/(${this.supportedLangs.join('|')})$`, 'i');
        if (regex.test(path)) return path;
        const normalized = path === '/' ? '/home' : path;
        return `${normalized}/en`;
    }

    /** La portada en inglés vive en '/': '/home/en' (o '/home') -> '/'. */
    static homeAlias(path: string): string {
        return /^\/home(\/en)?\/?$/i.test(path || '') ? '/' : path;
    }

    /** Devuelve URL absoluta a partir de un path relativo */
    buildAbsolute(path: string): string {
        try {
            const origin = this.getOrigin();
            const cleanPath = path.startsWith('/') ? path : `/${path}`;
            return `${origin}${cleanPath}`;
        } catch {
            return path;
        }
    }

    private getOrigin(): string {
        const browserOrigin = this.doc.location?.origin;
        if (browserOrigin && browserOrigin !== 'null' && !browserOrigin.startsWith('undefined')) {
            return browserOrigin;
        }

        const xfProto = (this.request?.headers?.['x-forwarded-proto'] || '').toString().split(',')[0].trim();
        const protocol = xfProto || this.request?.protocol || 'https';
        const xfHost = (this.request?.headers?.['x-forwarded-host'] || '').toString().split(',')[0].trim();
        const host = xfHost || this.request?.headers?.host || '';
        if (host) return `${protocol}://${host}`;

        return 'https://signaturetours.com.ar';
    }
}
